import { QuantizedMeshWorkerPool } from './qm-worker-pool.js';
import { QuantizedMeshData } from '../types/quantized-mesh-data.js';

export class QmDecoder {
  private readonly workerPool: QuantizedMeshWorkerPool;
  public initialTile: ArrayBuffer | null = null;
  public baseHeight: number | null = null;
  public maxHeight: number | null = null;
  public minHeight: number | null = null;
  public initialBaseHeight: number | null = null;
  /**
   * Bumped when terrain source data is invalidated (e.g. filter Apply).
   * In-flight fetches capture this at start and discard results if it changes.
   */
  public meshDataGeneration = 0;

  constructor(
    private readonly workerUrl: string,
    private readonly tileFetcher: (
      x: number,
      y: number,
      z: number,
      abortController: AbortController
    ) => Promise<{ data: ArrayBuffer; status: number }>,
    private readonly flatZoomLevels: number[] = [],
    baseHeight: number | null = null
  ) {
    this.workerPool = new QuantizedMeshWorkerPool(this.workerUrl);
    if (baseHeight !== null) {
      this.baseHeight = baseHeight;
    }
  }

  public bumpMeshDataGeneration(): void {
    this.meshDataGeneration++;
  }

  public dispose(): void {
    this.bumpMeshDataGeneration();
    this.workerPool.terminate();
    this.initialTile = null;
  }

  public async fetchAndDecode(
    tile: { x: number; y: number; z: number },
    abortController: AbortController
  ): Promise<QuantizedMeshData> {
    const generation = this.meshDataGeneration;
    const usesInitialTile =
      this.flatZoomLevels.includes(tile.z) && this.initialTile !== null;
    const { data, status } = usesInitialTile
      ? { data: this.initialTile!, status: 200 }
      : await this.tileFetcher(tile.x, tile.y, tile.z, abortController);

    this.throwIfStaleOrAborted(abortController, generation);

    // eslint-disable-next-line no-magic-numbers
    if (!data || status !== 200) {
      console.error(
        `Failed to fetch tile: ${JSON.stringify(tile)}, status: ${status}`
      );
      throw new Error(`Failed to fetch tile: ${JSON.stringify(tile)}`);
    }

    const initialTile = this.initialTile ? null : data.slice(0);
    // Copy before worker transfer — the fetcher may return a shared buffer (2D
    // empty tiles) and transfer detaches it, breaking concurrent decodes.
    const decodeBuffer = data.slice(0);
    const qmData = await this.processArrayBufferWithWorker(decodeBuffer);
    if (!qmData) {
      console.error('Failed to decode tile data');
      throw new Error(`Failed to decode tile data: ${JSON.stringify(tile)}`);
    }

    // Re-check after decode: invalidateTerrainData may have run while the
    // worker was busy. Writing initialTile / returning data would poison the
    // cache for the new filter (keys are tile coords only).
    this.throwIfStaleOrAborted(abortController, generation);

    if (!this.initialTile) {
      this.initialTile = initialTile;
    }

    if (this.initialBaseHeight === null) {
      // If baseHeight is already set, then we want to keep it (user-defined).
      if (this.baseHeight === null) {
        this.baseHeight = qmData.header.maxHeight; // Tiles of low zoom level are returned as empty tiles
      }
      if (this.maxHeight === null) {
        this.maxHeight = qmData.header.maxHeight;
      }
      if (this.minHeight === null) {
        this.minHeight = qmData.header.minHeight;
      }
      this.initialBaseHeight = qmData.header.maxHeight;
    }

    if (this.maxHeight !== null && this.minHeight !== null) {
      this.maxHeight =
        qmData.header.maxHeight > this.maxHeight
          ? qmData.header.maxHeight
          : this.maxHeight;

      this.minHeight =
        qmData.header.minHeight < this.minHeight
          ? qmData.header.minHeight
          : this.minHeight;
    }

    return qmData;
  }

  private throwIfStaleOrAborted(
    abortController: AbortController,
    generation: number
  ): void {
    if (
      abortController.signal.aborted ||
      generation !== this.meshDataGeneration
    ) {
      const error = new Error('Quantized mesh fetch aborted or invalidated');
      error.name = 'AbortError';
      throw error;
    }
  }

  private async processArrayBufferWithWorker(
    arrayBuffer: ArrayBuffer
  ): Promise<QuantizedMeshData | null> {
    const decodedGeometry = await this.workerPool.decode(arrayBuffer);
    return decodedGeometry;
  }
}
